import { Injectable } from '@angular/core';
import { CustomTemplate } from '../../models/CustomTemplate.model';
import { CustomSlug } from '../../models/CustomSlug.model';
import { CustomComponent } from '../../models/CustomComponent.model';

export interface FallbackTemplateConfig {
  slug: string;
  title: string;
  hasHeader?: boolean;
  hasFooter?: boolean;
  hasSidebar?: boolean;
  contentType: 'loading' | 'error' | 'offline' | 'minimal';
  customMessage?: string;
}

@Injectable({
  providedIn: 'root'
})
export class FallbackTemplateService {
  
  /**
   * Generate a fallback template when the main template fails to load
   */
  generateFallbackTemplate(config: FallbackTemplateConfig): CustomTemplate {
    const template = new CustomTemplate('', true, []);
    template.uuid = `fallback-${config.slug}`;
    template.name = `Fallback Template for ${config.slug}`;
    template.isstatic = true;
    template.customComponents = this.generateFallbackComponents(config);
    
    return template;
  }

  /**
   * Generate a fallback slug when the main slug fails to load
   */
  generateFallbackSlug(config: FallbackTemplateConfig): CustomSlug {
    const fallbackTemplateUuids = {
      1: `fallback-${config.slug}-desktop`,
      2: `fallback-${config.slug}-tablet`, 
      3: `fallback-${config.slug}-mobile`
    };
    
    const slug = new CustomSlug(
      config.slug,
      fallbackTemplateUuids, // static_template_uuid
      {1: null, 2: null, 3: null}, // dynamic_template_uuid (null for fallbacks)
      undefined, // id
      undefined, // uuid (will be auto-generated)
      false // isDevelopment
    );
    
    return slug;
  }

  /**
   * Get a complete fallback page data structure
   */
  getFallbackPageData(config: FallbackTemplateConfig): {
    customSlug: CustomSlug;
    customTemplateArr: CustomTemplate[];
  } {
    const fallbackSlug = this.generateFallbackSlug(config);
    
    // Create templates for each mode with matching UUIDs
    const desktopTemplate = this.generateFallbackTemplate({ ...config, slug: `${config.slug}-desktop` });
    const tabletTemplate = this.generateFallbackTemplate({ ...config, slug: `${config.slug}-tablet` });
    const mobileTemplate = this.generateFallbackTemplate({ ...config, slug: `${config.slug}-mobile` });
    
    // Set the correct UUIDs to match what's in the slug
    desktopTemplate.uuid = `fallback-${config.slug}-desktop`;
    tabletTemplate.uuid = `fallback-${config.slug}-tablet`;
    mobileTemplate.uuid = `fallback-${config.slug}-mobile`;

    return {
      customSlug: fallbackSlug,
      customTemplateArr: [desktopTemplate, tabletTemplate, mobileTemplate]
    };
  }

  /**
   * Generate components for the fallback template
   */
  private generateFallbackComponents(config: FallbackTemplateConfig): CustomComponent[] {
    const components: CustomComponent[] = [];
    let yPosition = 0;

    // Header component
    if (config.hasHeader !== false) {
      components.push(this.createHeaderComponent(yPosition));
      yPosition += 100;
    }

    // Main content area
    components.push(this.createMainContentComponent(config, yPosition));
    yPosition += 400;

    // Footer component
    if (config.hasFooter !== false) {
      components.push(this.createFooterComponent(yPosition));
    }

    return components;
  }

  private createHeaderComponent(yPosition: number): CustomComponent {
    const header = new CustomComponent(
      'FallbackHeader',
      0, yPosition, 12, 100,
      `fallback-header-${Date.now()}`,
      {},
      {
        backgroundColor: { type: 'string', value: '#f8f9fa' },
        borderBottom: { type: 'string', value: '1px solid #dee2e6' },
        padding: { type: 'string', value: '1rem' }
      },
      1
    );
    
    (header as any).innerHTML = `
      <div style="display: flex; align-items: center; justify-content: space-between; max-width: 1200px; margin: 0 auto;">
        <div style="display: flex; align-items: center;">
          <div style="width: 40px; height: 40px; background: #007bff; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold;">
            D
          </div>
          <span style="margin-left: 12px; font-size: 1.25rem; font-weight: 600; color: #343a40;">
            DUNA
          </span>
        </div>
        <div style="display: flex; gap: 1rem;">
          <div style="width: 80px; height: 20px; background: #e9ecef; border-radius: 4px; animation: pulse 1.5s ease-in-out infinite;"></div>
          <div style="width: 60px; height: 20px; background: #e9ecef; border-radius: 4px; animation: pulse 1.5s ease-in-out infinite;"></div>
        </div>
      </div>
    `;
    
    return header;
  }

  private createMainContentComponent(config: FallbackTemplateConfig, yPosition: number): CustomComponent {
    const content = new CustomComponent(
      'FallbackContent',
      0, yPosition, 12, 400,
      `fallback-content-${Date.now()}`,
      {},
      {
        padding: { type: 'string', value: '2rem' },
        minHeight: { type: 'string', value: '400px' },
        display: { type: 'string', value: 'flex' },
        flexDirection: { type: 'string', value: 'column' },
        justifyContent: { type: 'string', value: 'center' },
        alignItems: { type: 'string', value: 'center' }
      },
      1
    );

    let innerHTML = '';
    
    switch (config.contentType) {
      case 'loading':
        innerHTML = this.getLoadingContent(config);
        break;
      case 'error':
        innerHTML = this.getErrorContent(config);
        break;
      case 'offline':
        innerHTML = this.getOfflineContent(config);
        break;
      default:
        innerHTML = this.getMinimalContent(config);
    }

    (content as any).innerHTML = innerHTML + this.getAnimationStyles();
    return content;
  }

  private createFooterComponent(yPosition: number): CustomComponent {
    const footer = new CustomComponent(
      'FallbackFooter',
      0, yPosition, 12, 60,
      `fallback-footer-${Date.now()}`,
      {},
      {
        backgroundColor: { type: 'string', value: '#f8f9fa' },
        borderTop: { type: 'string', value: '1px solid #dee2e6' },
        padding: { type: 'string', value: '1rem' },
        textAlign: { type: 'string', value: 'center' },
        color: { type: 'string', value: '#6c757d' }
      },
      1
    );
    
    (footer as any).innerHTML = `
      <div style="max-width: 1200px; margin: 0 auto;">
        <p style="margin: 0; font-size: 0.875rem;">
          &copy; ${new Date().getFullYear()} DUNA - Digital Uddannelsesadministration
        </p>
      </div>
    `;
    
    return footer;
  }

  private getLoadingContent(config: FallbackTemplateConfig): string {
    return `
      <div style="text-align: center; max-width: 500px;">
        <div style="width: 64px; height: 64px; border: 4px solid #e9ecef; border-top: 4px solid #007bff; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 2rem;"></div>
        <h2 style="color: #343a40; margin-bottom: 1rem; font-size: 1.5rem;">Indlæser ${config.title}</h2>
        <p style="color: #6c757d; margin-bottom: 2rem; line-height: 1.5;">
          ${config.customMessage || 'Vi henter siden for dig. Dette tager normalt kun få sekunder.'}
        </p>
        <div style="display: flex; gap: 1rem; justify-content: center; margin-bottom: 2rem;">
          <div style="width: 120px; height: 20px; background: #e9ecef; border-radius: 4px; animation: pulse 1.5s ease-in-out infinite;"></div>
          <div style="width: 80px; height: 20px; background: #e9ecef; border-radius: 4px; animation: pulse 1.5s ease-in-out infinite;"></div>
        </div>
        <p style="color: #868e96; font-size: 0.875rem;">
          Hvis siden ikke indlæses, kan du prøve at opdatere siden.
        </p>
      </div>
    `;
  }

  private getErrorContent(config: FallbackTemplateConfig): string {
    return `
      <div style="text-align: center; max-width: 500px;">
        <div style="width: 64px; height: 64px; background: #dc3545; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 2rem; color: white; font-size: 24px;">
          ⚠
        </div>
        <h2 style="color: #343a40; margin-bottom: 1rem; font-size: 1.5rem;">Der opstod en fejl</h2>
        <p style="color: #6c757d; margin-bottom: 2rem; line-height: 1.5;">
          ${config.customMessage || `Vi kunne ikke indlæse siden "${config.title}". Dette kan skyldes et midlertidigt problem.`}
        </p>
        <div style="display: flex; gap: 1rem; justify-content: center;">
          <button onclick="location.reload()" style="background: #007bff; color: white; border: none; padding: 0.75rem 1.5rem; border-radius: 6px; cursor: pointer; font-weight: 500;">
            Prøv igen
          </button>
          <button onclick="history.back()" style="background: #6c757d; color: white; border: none; padding: 0.75rem 1.5rem; border-radius: 6px; cursor: pointer; font-weight: 500;">
            Gå tilbage
          </button>
        </div>
      </div>
    `;
  }

  private getOfflineContent(config: FallbackTemplateConfig): string {
    return `
      <div style="text-align: center; max-width: 500px;">
        <div style="width: 64px; height: 64px; background: #fd7e14; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 2rem; color: white; font-size: 24px;">
          📡
        </div>
        <h2 style="color: #343a40; margin-bottom: 1rem; font-size: 1.5rem;">Offline tilstand</h2>
        <p style="color: #6c757d; margin-bottom: 2rem; line-height: 1.5;">
          ${config.customMessage || `Du er i øjeblikket offline. "${config.title}" kræver en internetforbindelse for at vise det seneste indhold.`}
        </p>
        <div style="background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 6px; padding: 1rem; margin-bottom: 2rem; color: #856404;">
          <p style="margin: 0; font-size: 0.875rem;">
            Appen vil automatisk prøve at indlæse siden, når internetforbindelsen er genoprettet.
          </p>
        </div>
        <button onclick="location.reload()" style="background: #28a745; color: white; border: none; padding: 0.75rem 1.5rem; border-radius: 6px; cursor: pointer; font-weight: 500;">
          Prøv igen
        </button>
      </div>
    `;
  }

  private getMinimalContent(config: FallbackTemplateConfig): string {
    return `
      <div style="text-align: center; max-width: 500px;">
        <div style="width: 64px; height: 64px; background: #17a2b8; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 2rem; color: white; font-size: 24px;">
          📄
        </div>
        <h2 style="color: #343a40; margin-bottom: 1rem; font-size: 1.5rem;">${config.title}</h2>
        <p style="color: #6c757d; margin-bottom: 2rem; line-height: 1.5;">
          ${config.customMessage || 'Denne side er midlertidigt utilgængelig. Vi arbejder på at løse problemet.'}
        </p>
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 2rem;">
          <div style="background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 6px; padding: 1rem;">
            <div style="width: 100%; height: 120px; background: #e9ecef; border-radius: 4px; margin-bottom: 0.5rem; animation: pulse 1.5s ease-in-out infinite;"></div>
            <div style="width: 80%; height: 16px; background: #e9ecef; border-radius: 4px; margin-bottom: 0.5rem; animation: pulse 1.5s ease-in-out infinite;"></div>
            <div style="width: 60%; height: 16px; background: #e9ecef; border-radius: 4px; animation: pulse 1.5s ease-in-out infinite;"></div>
          </div>
        </div>
        <button onclick="location.reload()" style="background: #007bff; color: white; border: none; padding: 0.75rem 1.5rem; border-radius: 6px; cursor: pointer; font-weight: 500;">
          Opdater siden
        </button>
      </div>
    `;
  }

  private getAnimationStyles(): string {
    return `
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        button:hover {
          opacity: 0.9;
          transform: translateY(-1px);
          transition: all 0.2s ease;
        }
        
        @media (max-width: 768px) {
          .fallback-content {
            padding: 1rem !important;
          }
          
          h2 {
            font-size: 1.25rem !important;
          }
          
          button {
            width: 100%;
            margin-bottom: 0.5rem;
          }
        }
      </style>
    `;
  }

  /**
   * Get fallback configuration based on error type and slug
   */
  getFallbackConfig(slug: string, errorType: 'loading' | 'error' | 'offline' | 'notfound'): FallbackTemplateConfig {
    const slugTitleMap: { [key: string]: string } = {
      'dashboard': 'Dashboard',
      'ressourcer': 'Ressourcer',
      'lokationer': 'Lokationer',
      'brugere': 'Brugere',
      'events': 'Events',
      'profile': 'Profil',
      'indstillinger': 'Indstillinger',
      'login': 'Log ind',
      'kontakt': 'Kontakt',
      'faq': 'FAQ'
    };

    const title = slugTitleMap[slug] || slug.charAt(0).toUpperCase() + slug.slice(1);
    
    let contentType: 'loading' | 'error' | 'offline' | 'minimal' = 'error';
    let customMessage = '';

    switch (errorType) {
      case 'loading':
        contentType = 'loading';
        break;
      case 'offline':
        contentType = 'offline';
        break;
      case 'notfound':
        contentType = 'error';
        customMessage = `Siden "${title}" blev ikke fundet. Den kan være flyttet eller midlertidigt utilgængelig.`;
        break;
      default:
        contentType = 'minimal';
    }

    return {
      slug,
      title,
      hasHeader: true,
      hasFooter: true,
      hasSidebar: false,
      contentType,
      customMessage
    };
  }

  /**
   * Check if a fallback should be used based on error conditions
   */
  shouldUseFallback(error: any, isOffline: boolean): boolean {
    // Always use fallback if offline
    if (isOffline) {
      return true;
    }

    // Use fallback for specific error types
    if (error) {
      const errorMessage = error.message || '';
      const isNetworkError = errorMessage.includes('network') || 
                           errorMessage.includes('timeout') ||
                           errorMessage.includes('connection');
      
      return isNetworkError;
    }

    return false;
  }
} 